/**
 * Stale-chunk auto-recovery for the lazy-loaded SPA.
 *
 * Vite content-hashes every code-split chunk filename per build. After a
 * dashboard deploy the hashes change, so a browser tab still running the
 * PREVIOUS bundle will request a chunk URL that no longer exists the
 * moment the user navigates to a not-yet-loaded route. The dynamic
 * import rejects, and without recovery the whole React tree unmounts →
 * black screen until a manual reload (the 2026-05-30 regression report).
 *
 * Two complementary guards, both "reload once" so we never loop:
 *
 *  1. ``vite:preloadError`` — Vite dispatches this on ``window`` when its
 *     module-preload helper fails to fetch a chunk. We ``preventDefault``
 *     (stop Vite's default rethrow) and hard-reload to pull the fresh
 *     ``index.html`` + new chunk manifest.
 *
 *  2. A generic ``error`` / ``unhandledrejection`` sniff for the
 *     browser-specific "Failed to fetch dynamically imported module" /
 *     "error loading dynamically imported module" / "Importing a module
 *     script failed" messages — covers paths where the framework error
 *     doesn't surface as ``vite:preloadError`` (Safari, some Firefox
 *     versions, React.lazy's own rejection).
 *
 * A ``sessionStorage`` flag bounds us to ONE auto-reload per tab per
 * incident: if the reload didn't fix it (e.g. the chunk is genuinely
 * 500ing, not just stale), we stop and let the ErrorBoundary show a
 * manual-retry card instead of a reload loop.
 */

const RELOAD_FLAG = "finder:chunk-reloaded-at";
// If the last auto-reload was within this window, don't reload again —
// the problem isn't a stale bundle, so a loop won't help.
const RELOAD_DEBOUNCE_MS = 10_000;

function looksLikeStaleChunk(msg: string): boolean {
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /dynamically imported module/i.test(msg)
  );
}

function reloadOnce(): void {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_FLAG) || "0");
  } catch {
    // sessionStorage can throw in private-mode / sandboxed iframes —
    // fall back to reloading (better than a guaranteed black screen).
  }
  const now = Date.now();
  if (now - last < RELOAD_DEBOUNCE_MS) {
    // Already reloaded very recently and still failing → stop, let the
    // ErrorBoundary take over so the user isn't stuck in a refresh loop.
    return;
  }
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(now));
  } catch {
    /* ignore */
  }
  // ``location.reload()`` re-requests index.html, which references the
  // new content-hashed chunk filenames → stale references gone.
  window.location.reload();
}

/** Install the global stale-chunk listeners. Idempotent — safe to call once at boot. */
export function installChunkReloadGuard(): void {
  // Vite's dedicated event (preferred — fires before the rejection
  // becomes an unhandled error).
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    reloadOnce();
  });

  // Generic dynamic-import failure (React.lazy rejection that didn't go
  // through vite:preloadError on this browser).
  window.addEventListener("error", (e) => {
    const msg = (e?.message || "") + " " + ((e as ErrorEvent)?.error?.message || "");
    if (looksLikeStaleChunk(msg)) reloadOnce();
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e as PromiseRejectionEvent)?.reason;
    const msg = typeof reason === "string" ? reason : reason?.message || "";
    if (looksLikeStaleChunk(msg)) reloadOnce();
  });
}
